// src/components/Admin/CitasAdmin.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
    Box, Paper, Typography, Button, Snackbar, Checkbox, FormControlLabel,
    TextField, MenuItem, Accordion, AccordionSummary, AccordionDetails, Divider,
    Chip, Stack, Select, FormControl, InputLabel
} from "@mui/material";
import TablePagination from "@mui/material/TablePagination";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { LocalizationProvider, DatePicker, TimeField, DateCalendar } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/es";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import customParseFormat from "dayjs/plugin/customParseFormat";
import axios from "axios";

dayjs.extend(isSameOrBefore);
dayjs.extend(customParseFormat);

// Helpers
const isoLocal = (d) => (d ? dayjs(d).format("YYYY-MM-DD[T]HH:mm:ss") : null);
const pick = (o, ...ks) => { for (const k of ks) if (o && o[k] != null) return o[k]; return null; };
const toDay = (val) => {
    if (!val) return null;
    const s = String(val);
    if (s.includes("T")) return dayjs(s);
    const p = dayjs(s, "YYYY-MM-DD HH:mm:ss", true);
    return p.isValid() ? p : dayjs(s);
};
const fmtFechaYMD = (v) => {
    if (!v) return "";
    const d = dayjs(v, ["YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD HH:mm:ss"], true);
    return d.isValid() ? d.format("DD/MM/YYYY") : String(v);
};

const BLOQUEO_ADMIN_TAG = "[BLOQUEO_ADMIN]";

// NUEVOS HELPERS: conservación/limpieza de datos renta/cónyuge
const take = (obj, ...keys) => {
    for (const k of keys) {
        const v = obj?.[k];
        if (v !== undefined && v !== null) return v;
    }
    return null;
};

const extractRentaConyuge = (cita) => {
    const out = {
        // titular
        fechaCaducidadNif: take(cita, "fechaCaducidadNif", "fecha_caducidad_nif"),
        referenciaBorrador: take(cita, "referenciaBorrador", "referencia_borrador"),
        rentaAnterior: take(cita, "rentaAnterior", "renta_anterior"),
        // cónyuge
        nombreConyuge: take(cita, "nombreConyuge", "nombre_conyuge"),
        apellidosConyuge: take(cita, "apellidosConyuge", "apellidos_conyuge"),
        nifConyuge: take(cita, "nifConyuge", "nif_conyuge"),
        fechaCaducidadNifConyuge: take(cita, "fechaCaducidadNifConyuge", "fecha_caducidad_nif_conyuge"),
        referenciaBorradorConyuge: take(cita, "referenciaBorradorConyuge", "referencia_borrador_conyuge"),
    };
    return {
        ...out,
        // duplicados en snake para backend tolerante
        fecha_caducidad_nif: out.fechaCaducidadNif,
        referencia_borrador: out.referenciaBorrador,
        renta_anterior: out.rentaAnterior,
        nombre_conyuge: out.nombreConyuge,
        apellidos_conyuge: out.apellidosConyuge,
        nif_conyuge: out.nifConyuge,
        fecha_caducidad_nif_conyuge: out.fechaCaducidadNifConyuge,
        referencia_borrador_conyuge: out.referenciaBorradorConyuge,
    };
};

const clearRentaConyuge = () => ({
    fechaCaducidadNif: null, fecha_caducidad_nif: null,
    referenciaBorrador: null, referencia_borrador: null,
    rentaAnterior: null, renta_anterior: null,
    nombreConyuge: null, nombre_conyuge: null,
    apellidosConyuge: null, apellidos_conyuge: null,
    nifConyuge: null, nif_conyuge: null,
    fechaCaducidadNifConyuge: null, fecha_caducidad_nif_conyuge: null,
    referenciaBorradorConyuge: null, referencia_borrador_conyuge: null,
});

// intenta leer en cita.colegiado
const fromColegiado = (cita, ...aliases) => {
    const col = cita?.colegiado || {};
    for (const a of aliases) {
        if (col[a] != null) return col[a];
    }
    return null;
};

const DIA_OPCIONES = [
    { key: 1, label: "Lunes" }, { key: 2, label: "Martes" }, { key: 3, label: "Miércoles" },
    { key: 4, label: "Jueves" }, { key: 5, label: "Viernes" }, { key: 6, label: "Sábado" }, { key: 0, label: "Domingo" },
];
const DURACIONES = [10, 15, 20, 30, 45, 60];
const TURNOS = [
    { value: 1, label: "Turno simple" },
    { value: 2, label: "Doble turno" },
    { value: 3, label: "Triple turno" },
];
const TIPOS_CITA = ["CITA PRESENCIAL", "CITA TELEFÓNICA"];
const TIPO_NONE = "SIN_TIPO";

// === helpers de día (NUEVOS) ===
const makeDefaultPerDia = ({ dur = 15, turnos = 1, usarTarde = false, hmIni = "09:00", hmFin = "14:00", htIni = "16:00", htFin = "19:00" } = {}) =>
    DIA_OPCIONES.reduce((acc, d) => {
        acc[d.key] = {
            duracionMin: dur,
            turnosPorBloque: turnos,
            usarTarde,
            horaInicioManana: hmIni,
            horaFinManana: hmFin,
            horaInicioTarde: htIni,
            horaFinTarde: htFin,
        };
        return acc;
    }, {});

const getDuracionForDow = (cfg, dow) => cfg?.perDia?.[dow]?.duracionMin ?? cfg?.duracionMin ?? 15;
const getTurnosForDow = (cfg, dow) => cfg?.perDia?.[dow]?.turnosPorBloque ?? cfg?.turnosPorBloque ?? 1;

// convierte "HH:mm" o "HH:mm:ss" a dayjs sobre una fecha base
const timeToDayjsOn = (baseDay, hhmm) => {
    if (!baseDay) baseDay = dayjs();
    const s = String(hhmm || "");
    if (!s) return null;
    const [h, m] = s.split(":").map(Number);
    return dayjs(baseDay).hour(h || 0).minute(m || 0).second(0).millisecond(0);
};

export default function CitasAdmin() {
    const API = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("token");

    const headers = useMemo(() => ({
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
    }), [token]);

    const [mensaje, setMensaje] = useState("");
    const [generando, setGenerando] = useState(false);

    // ---- Caché de colegiados (para tarjetas y CSV)
    const cacheRef = useRef(new Map());
    const getColegiadoFromCache = (codigo) => cacheRef.current.get(String(codigo));
    const setColegiadoInCache = (codigo, info) => cacheRef.current.set(String(codigo), info);

    const normalizeColegiado = (raw = {}) => ({
        codigo: raw.codigo ?? raw.id ?? raw.code ?? null,
        nombre: raw.nombre ?? raw.name ?? raw.firstName ?? "",
        apellidos:
            raw.apellidos ??
            raw.apellido ??
            raw.lastName ??
            [raw.apellido_1, raw.apellido_2, raw.primerApellido, raw.segundoApellido]
                .filter(Boolean)
                .join(" ")
                .trim(),
        email: raw.email ?? raw.email_2 ?? raw.email_3 ?? raw.correo ?? raw.mail ?? "",
        telefono:
            raw.telefono ??
            raw.telefono_1 ??
            raw.telefono_2 ??
            raw.telefono_3 ??
            raw.telefono1 ??
            raw.telefonoMovil ??
            raw.movil ??
            raw.phone ??
            raw.phoneNumber ??
            "",
        telefono_2:
            raw.telefono_2 ??
            raw.telefono2 ??
            "",
        nif: raw.nif ?? raw.dni ?? raw.nifNie ?? raw.nif_nie ?? raw.documento ?? "",
    });

    const ensureColegiadoLoaded = async (codigo) => {
        const key = String(codigo || "");
        if (!key) return null;
        const cached = getColegiadoFromCache(key);
        if (cached) return cached;
        const url = `${API}/api/admin/colegiados/${key}`;
        const { data } = await axios.get(url, { headers });
        const norm = normalizeColegiado(data || {});
        setColegiadoInCache(key, norm);
        return norm;
    };

    // --- Config panel (CON perDia)
    const [config, setConfig] = useState({
        fechaDesde: dayjs().startOf("month"),
        fechaHasta: dayjs().add(2, "month").endOf("month"),
        diasLaborables: [1, 2, 3, 4, 5],
        // globales legacy (compat.)
        duracionMin: 15,
        turnosPorBloque: 1,
        usarTarde: false, // legacy (solo para migración)
        horaInicioManana: dayjs().hour(9).minute(0).second(0), // legacy
        horaFinManana: dayjs().hour(14).minute(0).second(0),   // legacy
        horaInicioTarde: dayjs().hour(16).minute(0).second(0), // legacy
        horaFinTarde: dayjs().hour(19).minute(0).second(0),    // legacy

        // nuevos por día:
        perDia: makeDefaultPerDia({ dur: 15, turnos: 1, usarTarde: false }),

        permitirConyuge: true,
        festivos: [],
        tiposSemana: {}
    });

    // Datos
    const [citas, setCitas] = useState([]);
    const [loadingCitas, setLoadingCitas] = useState(false);

    // Asignación (cabecera grande)
    const [adminColegiadoCodigo, setAdminColegiadoCodigo] = useState("");
    const [selDate, setSelDate] = useState(dayjs());
    const [huecosDia, setHuecosDia] = useState([]);
    const [slotSel, setSlotSel] = useState(null);
    const [minFechaCitas, setMinFechaCitas] = useState(null);
    const [maxFechaCitas, setMaxFechaCitas] = useState(null);

    // Formulario (aparece en un Paper aparte cuando hay código)
    const [adminDescripcion, setAdminDescripcion] = useState("");
    const [fechaCaducidadNif, setFechaCaducidadNif] = useState(null);
    const [referenciaBorrador, setReferenciaBorrador] = useState("");
    const [rentaAnterior, setRentaAnterior] = useState(false);

    const [usarConyuge, setUsarConyuge] = useState(false);
    const [nombreConyuge, setNombreConyuge] = useState("");
    const [apellidosConyuge, setApellidosConyuge] = useState("");
    const [nifConyuge, setNifConyuge] = useState("");
    const [fechaCaducidadNifConyuge, setFechaCaducidadNifConyuge] = useState(null);
    const [referenciaBorradorConyuge, setReferenciaBorradorConyuge] = useState("");

    // Aux
    const [festivoInput, setFestivoInput] = useState("");
    const [pendPage, setPendPage] = useState(0);
    const [pendRows, setPendRows] = useState(10);
    const [confPage, setConfPage] = useState(0);
    const [confRows, setConfRows] = useState(10);
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        if (config.fechaDesde) setFestivoInput(dayjs(config.fechaDesde).format("DD/MM/YYYY"));
    }, [config.fechaDesde]);

    // Cargar config (soporta perDia y mantiene compat. con globales)
    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/api/citas/config`, { headers });

                // legacy -> perDia
                const legacyDur = data.duracionMin ?? 15;
                const legacyTurnos = data.turnosPorBloque ?? 1;
                const legacyUsarTarde = !!data.usarTarde;

                // strings "HH:mm"
                const leg_hmIni = (data.horaInicioManana || data.horaInicio || "09:00");
                const leg_hmFin = (data.horaFinManana || data.horaFin || "14:00");
                const leg_htIni = (data.horaInicioTarde || "16:00");
                const leg_htFin = (data.horaFinTarde || "19:00");

                const perDiaLoaded = data.perDia && typeof data.perDia === "object"
                    ? data.perDia
                    : makeDefaultPerDia({
                        dur: legacyDur,
                        turnos: legacyTurnos,
                        usarTarde: legacyUsarTarde,
                        hmIni: leg_hmIni, hmFin: leg_hmFin,
                        htIni: leg_htIni, htFin: leg_htFin
                    });

                setConfig((c) => ({
                    ...c,
                    fechaDesde: data.fechaDesde ? dayjs(data.fechaDesde) : c.fechaDesde,
                    fechaHasta: data.fechaHasta ? dayjs(data.fechaHasta) : c.fechaHasta,
                    diasLaborables: Array.isArray(data.diasLaborables) ? data.diasLaborables : c.diasLaborables,

                    // legacy (se mantienen por compat, pero la UI usa perDia)
                    duracionMin: legacyDur,
                    turnosPorBloque: legacyTurnos,
                    usarTarde: legacyUsarTarde,
                    horaInicioManana: timeToDayjsOn(dayjs(), leg_hmIni),
                    horaFinManana: timeToDayjsOn(dayjs(), leg_hmFin),
                    horaInicioTarde: timeToDayjsOn(dayjs(), leg_htIni),
                    horaFinTarde: timeToDayjsOn(dayjs(), leg_htFin),

                    perDia: perDiaLoaded,
                    permitirConyuge: data.permitirConyuge ?? c.permitirConyuge,
                    tiposSemana: data.tiposSemana ?? c.tiposSemana,
                    festivos: Array.isArray(data.festivos) ? data.festivos.map(f => dayjs(f)) : c.festivos
                }));
            } catch {
                /* opcional */
            }
        })();
    }, [API, headers]);

    // Cargar todas las citas
    const cargarCitas = async () => {
        setLoadingCitas(true);
        try {
            const { data } = await axios.get(`${API}/api/citas`, { headers });
            const lista = Array.isArray(data) ? data : [];
            lista.sort((a, b) => {
                const aFi = toDay(pick(a, "fechaInicio", "fecha_inicio"))?.valueOf() ?? 0;
                const bFi = toDay(pick(b, "fechaInicio", "fecha_inicio"))?.valueOf() ?? 0;
                return aFi - bFi;
            });

            const iniciosValidosMs = lista
                .map(c => toDay(pick(c, "fechaInicio", "fecha_inicio")))
                .filter(d => d && d.isValid())
                .map(d => d.startOf("day").valueOf());

            if (iniciosValidosMs.length) {
                const min = dayjs(Math.min(...iniciosValidosMs)).startOf("day");
                const max = dayjs(Math.max(...iniciosValidosMs)).startOf("day");
                setMinFechaCitas(min);
                setMaxFechaCitas(max);
                if (!selDate || selDate.isBefore(min, "day") || selDate.isAfter(max, "day")) {
                    setSelDate(min);
                }
            }
            setCitas(lista);

            const ymd = dayjs(selDate).format("YYYY-MM-DD");
            setHuecosDia(
                lista
                    .filter((c) => {
                        const fi = toDay(pick(c, "fechaInicio", "fecha_inicio"));
                        return fi && fi.format("YYYY-MM-DD") === ymd && (c.titulo || "").toUpperCase() === "DISPONIBLE";
                    })
                    .sort((a, b) => {
                        const aFi = toDay(pick(a, "fechaInicio", "fecha_inicio"))?.valueOf() ?? 0;
                        const bFi = toDay(pick(b, "fechaInicio", "fecha_inicio"))?.valueOf() ?? 0;
                        return aFi - bFi;
                    })
            );
            setSlotSel(null);
        } catch (e) {
            console.error(e);
            setMensaje("No se pudieron cargar las citas.");
            setCitas([]);
        } finally {
            setLoadingCitas(false);
        }
    };
    useEffect(() => { cargarCitas(); /* eslint-disable-next-line */ }, []);

    // Recalcular huecos por fecha
    useEffect(() => {
        const ymd = dayjs(selDate).format("YYYY-MM-DD");
        setHuecosDia(
            citas
                .filter((c) => {
                    const fi = toDay(pick(c, "fechaInicio", "fecha_inicio"));
                    return fi && fi.format("YYYY-MM-DD") === ymd && (c.titulo || "").toUpperCase() === "DISPONIBLE";
                })
                .sort((a, b) => {
                    const aFi = toDay(pick(a, "fechaInicio", "fecha_inicio"))?.valueOf() ?? 0;
                    const bFi = toDay(pick(b, "fechaInicio", "fecha_inicio"))?.valueOf() ?? 0;
                    return aFi - bFi;
                })
        );
        setSlotSel(null);
    }, [selDate, citas]);

    // Festivos & tipo por día
    const addFestivo = () => {
        const d = dayjs(festivoInput, "DD/MM/YYYY", true);
        if (!d.isValid()) return setMensaje("Fecha de festivo inválida (usa DD/MM/YYYY).");
        const ymd = d.format("YYYY-MM-DD");
        const exists = (config.festivos || []).some(f => dayjs(f).format("YYYY-MM-DD") === ymd);
        if (exists) return setMensaje("Esa fecha ya está en la lista de festivos.");
        setConfig(c => ({ ...c, festivos: [...(c.festivos || []), d] }));
        setFestivoInput("");
    };
    const removeFestivo = (ymd) => {
        setConfig(c => ({ ...c, festivos: (c.festivos || []).filter(f => dayjs(f).format("YYYY-MM-DD") !== ymd) }));
    };
    const changeTipoSemana = (dow, value) => {
        setConfig(c => ({ ...c, tiposSemana: { ...(c.tiposSemana || {}), [dow]: value } }));
    };

    // =============== generarHuecos (usa configuración POR DÍA) ===============
    const generarHuecos = async () => {
        const {
            fechaDesde, fechaHasta, diasLaborables, festivos, tiposSemana, perDia
        } = config;

        if (!fechaDesde || !fechaHasta) return setMensaje("Completa rango de fechas.");
        if (dayjs(fechaHasta).isBefore(dayjs(fechaDesde))) return setMensaje("Rango de fechas inválido.");

        // Validaciones por día
        for (const { key: dow, label } of DIA_OPCIONES) {
            if (!diasLaborables.includes(dow)) continue;
            const dcfg = perDia?.[dow];
            if (!dcfg) return setMensaje(`Falta configuración del día ${label}.`);
            const hmIni = timeToDayjsOn(dayjs(), dcfg.horaInicioManana);
            const hmFin = timeToDayjsOn(dayjs(), dcfg.horaFinManana);
            if (!hmIni || !hmFin || !hmFin.isAfter(hmIni)) {
                return setMensaje(`Mañana inválida en ${label}.`);
            }
            if (dcfg.usarTarde) {
                const htIni = timeToDayjsOn(dayjs(), dcfg.horaInicioTarde);
                const htFin = timeToDayjsOn(dayjs(), dcfg.horaFinTarde);
                if (!htIni || !htFin || !htFin.isAfter(htIni)) {
                    return setMensaje(`Tarde inválida en ${label}.`);
                }
            }
            if (!dcfg.duracionMin || dcfg.duracionMin <= 0) {
                return setMensaje(`Duración inválida en ${label}.`);
            }
        }

        const ok = window.confirm("Se eliminarán huecos antiguos y se crearán los nuevos.\n\n¿Desea continuar?");
        if (!ok) return setMensaje("Operación cancelada.");

        setGenerando(true);
        try {
            await axios.delete(`${API}/api/citas/admin/clear`, { headers });

            const lotes = [];
            const addIntervalo = (diaBase, iniStr, finStr, duracionMin, turnosPorBloque, descripcionDelDia) => {
                const ini = timeToDayjsOn(diaBase, iniStr);
                const fin = timeToDayjsOn(diaBase, finStr);
                let cursor = ini;
                while (true) {
                    const siguiente = cursor.add(duracionMin, "minute");
                    if (!(siguiente.isBefore(fin) || siguiente.isSame(fin))) break;
                    for (let i = 0; i < (turnosPorBloque || 1); i++) {
                        lotes.push({
                            titulo: "DISPONIBLE",
                            descripcion: descripcionDelDia || "Hueco disponible",
                            fechaInicio: isoLocal(cursor),
                            fechaFin: isoLocal(siguiente),
                        });
                    }
                    cursor = siguiente;
                }
            };

            const festivosSet = new Set((festivos || []).map(f => dayjs(f).format("YYYY-MM-DD")));
            let d = dayjs(fechaDesde).startOf("day");
            const finRango = dayjs(fechaHasta).endOf("day");
            while (d.isSameOrBefore(finRango, "day")) {
                const ymd = d.format("YYYY-MM-DD");
                const dow = d.day();
                const tipo = (tiposSemana && tiposSemana[dow]) || TIPO_NONE;
                const descripcionDelDia = (tipo && tipo !== TIPO_NONE) ? tipo : null;

                if (diasLaborables.includes(dow) && !festivosSet.has(ymd)) {
                    const dcfg = perDia?.[dow] || {};
                    const dur = dcfg.duracionMin ?? 15;
                    const tpb = dcfg.turnosPorBloque ?? 1;

                    // mañana
                    addIntervalo(d, dcfg.horaInicioManana, dcfg.horaFinManana, dur, tpb, descripcionDelDia);
                    // tarde (si se activa ese día)
                    if (dcfg.usarTarde) {
                        addIntervalo(d, dcfg.horaInicioTarde, dcfg.horaFinTarde, dur, tpb, descripcionDelDia);
                    }
                }
                d = d.add(1, "day");
            }

            for (const h of lotes) {
                const body = {
                    titulo: h.titulo,
                    descripcion: h.descripcion,
                    fechaInicio: h.fechaInicio,
                    fechaFin: h.fechaFin,
                    fecha_inicio: h.fechaInicio,
                    fecha_fin: h.fechaFin,
                };
                // eslint-disable-next-line no-await-in-loop
                await axios.post(`${API}/api/citas`, body, { headers });
            }

            setMensaje(`Huecos generados: ${lotes.length}`);
            await cargarCitas();
        } catch (e) {
            console.error(e);
            setMensaje("Error generando huecos.");
        } finally {
            setGenerando(false);
        }
    };
    // =======================================================

    // ——— util: siguiente hueco consecutivo DISPONIBLE (para cónyuge)
    const findNextConsecutiveDisponible = (slotsDelDia, slotActual) => {
        if (!slotActual) return null;
        const finActual = toDay(pick(slotActual, "fechaFin", "fecha_fin"));
        if (!finActual) return null;
        const finMs = finActual.valueOf();
        return (
            slotsDelDia.find((s) => {
                const ini = toDay(pick(s, "fechaInicio", "fecha_inicio"));
                const titulo = String(s.titulo || "").toUpperCase();
                const coleg =
                    s?.colegiado?.codigo ?? s?.colegiadoCodigo ?? s?.colegiado_id ?? s?.colegiadoId ?? null;
                return ini && ini.valueOf() === finMs && titulo === "DISPONIBLE" && (coleg == null);
            }) || null
        );
    };

    const addRentaFields = (obj = {}) => ({
        ...obj,
        // titular
        fechaCaducidadNif: fechaCaducidadNif ? dayjs(fechaCaducidadNif).format("YYYY-MM-DD") : null,
        fecha_caducidad_nif: fechaCaducidadNif ? dayjs(fechaCaducidadNif).format("YYYY-MM-DD") : null,
        referenciaBorrador: referenciaBorrador || null,
        referencia_borrador: referenciaBorrador || null,
        rentaAnterior: !!rentaAnterior,
        renta_anterior: !!rentaAnterior,
        // cónyuge
        nombreConyuge: usarConyuge ? (nombreConyuge || null) : null,
        apellidosConyuge: usarConyuge ? (apellidosConyuge || null) : null,
        nifConyuge: usarConyuge ? (nifConyuge || null) : null,
        fechaCaducidadNifConyuge: usarConyuge && fechaCaducidadNifConyuge ? dayjs(fechaCaducidadNifConyuge).format("YYYY-MM-DD") : null,
        referenciaBorradorConyuge: usarConyuge ? (referenciaBorradorConyuge || null) : null,
        // snake dup
        nombre_conyuge: usarConyuge ? (nombreConyuge || null) : null,
        apellidos_conyuge: usarConyuge ? (apellidosConyuge || null) : null,
        nif_conyuge: usarConyuge ? (nifConyuge || null) : null,
        fecha_caducidad_nif_conyuge: usarConyuge && fechaCaducidadNifConyuge ? dayjs(fechaCaducidadNifConyuge).format("YYYY-MM-DD") : null,
        referencia_borrador_conyuge: usarConyuge ? (referenciaBorradorConyuge || null) : null,
    });

    // Asignar hueco
    const asignarHueco = async () => {
        const codigo = Number(adminColegiadoCodigo || 0);
        if (!codigo) return setMensaje("Introduce un código de colegiado.");
        if (!slotSel?.id) return setMensaje("Selecciona un hueco DISPONIBLE.");

        try {
            const fi = toDay(pick(slotSel, "fechaInicio", "fecha_inicio"));
            const ff = toDay(pick(slotSel, "fechaFin", "fecha_fin"));

            // Si usa cónyuge, necesitamos un hueco consecutivo inmediatamente después
            let consecutivo = null;
            if (usarConyuge) {
                consecutivo = findNextConsecutiveDisponible(huecosDia, slotSel);
                if (!consecutivo) {
                    return setMensaje("No existe un hueco consecutivo inmediato para cónyuge.");
                }
            }

            const bodyPrincipal = addRentaFields({
                titulo: "CONFIRMADA",
                descripcion: (adminDescripcion?.trim() || slotSel.descripcion || "Asignada por administración"),
                fechaInicio: isoLocal(fi),
                fechaFin: isoLocal(ff),
                colegiadoCodigo: codigo,
                colegiadoId: codigo,
                colegiado_id: codigo,
                colegiado: { codigo },
            });

            const putCita = async (slot, payload) =>
                axios.put(`${API}/api/citas/${slot.id}`, payload, { headers });

            if (consecutivo) {
                const fi2 = toDay(pick(consecutivo, "fechaInicio", "fecha_inicio"));
                const ff2 = toDay(pick(consecutivo, "fechaFin", "fecha_fin"));
                const bodyConyuge = addRentaFields({
                    titulo: "CONFIRMADA",
                    descripcion: consecutivo?.descripcion || (adminDescripcion?.trim() || slotSel.descripcion || "Asignada por administración"),
                    fechaInicio: isoLocal(fi2),
                    fechaFin: isoLocal(ff2),
                    colegiadoCodigo: codigo,
                    colegiadoId: codigo,
                    colegiado_id: codigo,
                    colegiado: { codigo },
                });
                await putCita(consecutivo, bodyConyuge);
                await putCita(slotSel, bodyPrincipal);
            } else {
                await putCita(slotSel, bodyPrincipal);
            }

            setMensaje(`Hueco${consecutivo ? "s" : ""} asignado${consecutivo ? "s" : ""} al colegiado ${codigo}.`);
            setSlotSel(null);

            // limpia formulario
            setAdminDescripcion("");
            setFechaCaducidadNif(null);
            setReferenciaBorrador("");
            setRentaAnterior(false);
            setUsarConyuge(false);
            setNombreConyuge("");
            setApellidosConyuge("");
            setNifConyuge("");
            setFechaCaducidadNifConyuge(null);
            setReferenciaBorradorConyuge("");

            await cargarCitas();
        } catch (e) {
            console.error(e);
            setMensaje("No se pudo asignar el hueco.");
        }
    };

    const pendientes = citas.filter((c) => (c.titulo || "").toUpperCase() === "PENDIENTE");
    const confirmadas = citas.filter((c) => (c.titulo || "").toUpperCase() === "CONFIRMADA");

    const pendSlice = useMemo(
        () => pendientes.slice(pendPage * pendRows, pendPage * pendRows + pendRows),
        [pendientes, pendPage, pendRows]
    );
    const confSlice = useMemo(
        () => confirmadas.slice(confPage * confRows, confPage * confRows + confRows),
        [confirmadas, confPage, confRows]
    );

    // Precargar colegiado al expandir tarjeta
    useEffect(() => {
        if (!expandedId) return;
        const cita = citas.find(x => x.id === expandedId);
        const cod = cita?.colegiado?.codigo ?? cita?.colegiadoCodigo ?? cita?.colegiado_id ?? cita?.colegiadoId;
        if (cod && !getColegiadoFromCache(String(cod))) {
            ensureColegiadoLoaded(cod).catch(() => { });
        }
    }, [expandedId]);

    // Export CSV
    const csvEscape = (val) => {
        const s = String(val ?? "");
        const needsQuotes = /[;"\n\r,]/.test(s);
        const escaped = s.replace(/"/g, '""');
        return needsQuotes ? `"${escaped}"` : escaped;
    };

    const exportConfirmadasCSV = async (soloPagina = false) => {
        const sep = ";";
        const header = [
            "ID", "Fecha inicio", "Fecha fin", "Estado",
            "Coleg. Código",
            "Titular - Nombre", "Titular - Apellidos", "Titular - Email",
            "Titular - Teléfono 1", "Titular - Teléfono 2", "Titular - NIF/NIE",
            "Titular - Cad. NIF", "Titular - Ref IRPF", "Renta Año Anterior",
            "Cónyuge - Nombre", "Cónyuge - Apellidos", "Cónyuge - NIF/NIE", "Cónyuge - Cad. NIF", "Cónyuge - Ref IRPF",
            "Descripción"
        ].map(csvEscape).join(sep);

        const fuente = soloPagina ? confSlice : confirmadas;

        const toFetch = [];
        for (const c of fuente) {
            const cod = c?.colegiado?.codigo ?? c?.colegiadoCodigo ?? c?.colegiado_id ?? c?.colegiadoId;
            if (!cod) continue;
            const emb = c?.colegiado || {};
            const enough =
                (emb.nombre || emb.name || emb.firstName) &&
                (emb.apellidos || emb.apellido || emb.lastName || emb.primerApellido) &&
                (emb.email || emb.correo || emb.mail) &&
                (
                    emb.telefono ||
                    emb.telefono_1 ||
                    emb.telefono_2 ||
                    emb.telefono1 ||
                    emb.telefonoMovil ||
                    emb.movil ||
                    emb.phone
                ) &&
                (emb.nif || emb.dni || emb.nifNie || emb.nif_nie || emb.documento);
            if (!enough && !getColegiadoFromCache(String(cod))) toFetch.push(String(cod));
        }
        for (const code of toFetch) {
            try { await ensureColegiadoLoaded(code); } catch { }
        }

        const rows = fuente.map((c) => {
            const fi = toDay(pick(c, "fechaInicio", "fecha_inicio"));
            const ff = toDay(pick(c, "fechaFin", "fecha_fin"));
            const cod = c?.colegiado?.codigo ?? c?.colegiadoCodigo ?? c?.colegiado_id ?? c?.colegiadoId ?? "";
            const emb = c?.colegiado || {};
            const cached = getColegiadoFromCache(String(cod)) || {};
            const nom = cached.nombre || emb.nombre || emb.name || emb.firstName || "";
            const ape = cached.apellidos || ([emb.apellidos, emb.apellido, emb.lastName, emb.apellido_1, emb.apellido_2, emb.primerApellido, emb.segundoApellido].filter(Boolean).join(" ").trim()) || "";
            const email = cached.email || emb.email || emb.email_2 || emb.email_3 || emb.correo || emb.mail || "";
            const tel1 =
                cached.telefono_1 ||
                emb.telefono_1 ||
                emb.telefono ||
                emb.telefono1 ||
                emb.telefonoMovil ||
                emb.movil ||
                emb.phone ||
                emb.phoneNumber ||
                "";

            const tel2 =
                cached.telefono_2 ||
                emb.telefono_2 ||
                emb.telefono2 ||
                "";
            const nif = cached.nif || emb.nif || emb.dni || emb.nifNie || emb.nif_nie || emb.documento || "";

            const fcTitRaw = pick(c, "fechaCaducidadNif", "fecha_caducidad_nif");
            const fcTit = fmtFechaYMD(fcTitRaw);
            const refTit = pick(c, "referenciaBorrador", "referencia_borrador") || "";
            const rentaAntRaw = pick(c, "rentaAnterior", "renta_anterior");
            const rentaAntTxt = (rentaAntRaw === true || rentaAntRaw === 1 || rentaAntRaw === "1") ? "Sí" :
                (rentaAntRaw === false || rentaAntRaw === 0 || rentaAntRaw === "0") ? "No" : "";

            const nomC = pick(c, "nombreConyuge", "nombre_conyuge") || "";
            const apeC = pick(c, "apellidosConyuge", "apellidos_conyuge") || "";
            const nifC = pick(c, "nifConyuge", "nif_conyuge") || "";
            const fcCRaw = pick(c, "fechaCaducidadNifConyuge", "fecha_caducidad_nif_conyuge");
            const fcC = fmtFechaYMD(fcCRaw);
            const refC = pick(c, "referenciaBorradorConyuge", "referencia_borrador_conyuge") || "";

            const cells = [
                c.id ?? "", fi ? fi.format("DD/MM/YYYY HH:mm") : "", ff ? ff.format("DD/MM/YYYY HH:mm") : "",
                c.titulo ?? "", cod, nom, ape, email, tel1, tel2, nif,
                fcTit, refTit, rentaAntTxt,
                nomC, apeC, nifC, fcC, refC,
                c.descripcion ?? "",
            ].map(csvEscape);
            return cells.join(sep);
        });

        const csv = "\ufeff" + [header, ...rows].join("\r\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const sufijo = soloPagina ? "_pagina" : "_todas";
        a.download = `citas_confirmadas${sufijo}_${dayjs().format("YYYYMMDD_HHmm")}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(pendientes.length / pendRows) - 1);
        if (pendPage > maxPage) setPendPage(0);
    }, [pendientes.length, pendRows, pendPage]);

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(confirmadas.length / confRows) - 1);
        if (confPage > maxPage) setConfPage(0);
    }, [confirmadas.length, confRows, confPage]);

    // === Bloques de detalle (tarjetas listas)
    const RentaConyugeBlock = ({ c }) => {
        const get = (a, b) => pick(c, a, b);
        const rentaAnt = get("rentaAnterior", "renta_anterior");
        const fcTit = get("fechaCaducidadNif", "fecha_caducidad_nif");
        const refTit = get("referenciaBorrador", "referencia_borrador");

        const nomC = get("nombreConyuge", "nombre_conyuge");
        const apeC = get("apellidosConyuge", "apellidos_conyuge");
        const nifC = get("nifConyuge", "nif_conyuge");
        const fcC = get("fechaCaducidadNifConyuge", "fecha_caducidad_nif_conyuge");
        const refC = get("referenciaBorradorConyuge", "referencia_borrador_conyuge");

        const anyRenta = fcTit || refTit || typeof rentaAnt === "boolean";
        const anyCony = nomC || apeC || nifC || fcC || refC;
        if (!anyRenta && !anyCony) return null;

        return (
            <Box sx={{ mt: .5 }}>
                {anyRenta && (
                    <Typography variant="body2" sx={{ opacity: .9 }}>
                        <strong>Renta:</strong> Caduc. NIF: {fcTit || "—"} · Ref IRPF: {refTit || "—"} · Año anterior: {rentaAnt ? "Sí" : "No"}
                    </Typography>
                )}
                {anyCony && (
                    <Typography variant="body2" sx={{ opacity: .9 }}>
                        <strong>Cónyuge:</strong> {nomC || "—"} {apeC || ""} · NIF: {nifC || "—"} · Cad.: {fcC || "—"} · Ref IRPF: {refC || "—"}
                    </Typography>
                )}
            </Box>
        );
    };

    const DetalleColegiado = ({ c }) => {
        const cod = c?.colegiado?.codigo ?? c?.colegiadoCodigo ?? c?.colegiado_id ?? c?.colegiadoId;
        const cached = getColegiadoFromCache(String(cod));

        useEffect(() => {
            if (cod && !cached) ensureColegiadoLoaded(cod).catch(() => { });
        }, [cod]); // eslint-disable-line

        const fi = toDay(pick(c, "fechaInicio", "fecha_inicio"));
        const ff = toDay(pick(c, "fechaFin", "fecha_fin"));

        const nombre = (cached?.nombre || fromColegiado(c, "nombre", "name")) || "—";
        const apellidos =
            cached?.apellidos ||
            [fromColegiado(c, "apellidos", "apellido", "lastName", "apellido_1"), fromColegiado(c, "apellido_2")]
                .filter(Boolean)
                .join(" ")
                .trim() ||
            "—";
        const email = cached?.email || fromColegiado(c, "email", "email_2", "email_3", "correo", "mail") || "—";
        const telefono1 =
            cached?.telefono_1 ||
            fromColegiado(c, "telefono_1", "telefono", "telefono_3", "phone") ||
            "—";

        const telefono2 =
            cached?.telefono_2 ||
            fromColegiado(c, "telefono_2") ||
            "—";
        const nif = (cached?.nif || fromColegiado(c, "nif", "dni", "nifNie", "nif_nie")) || "—";

        return (
            <Box sx={{ mt: 1.0, p: 1.2, border: "1px dashed #ddd", borderRadius: 1 }}>
                <Typography variant="body2"><strong>{nombre} {apellidos}</strong> · #{c.id}</Typography>
                <Typography variant="body2" sx={{ opacity: .85 }}>
                    {fi ? fi.format("DD/MM/YYYY HH:mm") : "—"} – {ff ? ff.format("HH:mm") : "—"}
                </Typography>
                <Typography variant="body2" sx={{ opacity: .9 }}>Email: {email}</Typography>
                <Typography variant="body2" sx={{ opacity: .9 }}>Teléfono 1: {telefono1}</Typography>
                <Typography variant="body2" sx={{ opacity: .9 }}>Teléfono 2: {telefono2}</Typography>
                <Typography variant="body2" sx={{ opacity: .9 }}>NIF/NIE: {nif}</Typography>
                <RentaConyugeBlock c={c} />
            </Box>
        );
    };

    const hasAdminCode = adminColegiadoCodigo.trim().length > 0;

    // === REEMPLAZADO: ahora preserva datos renta/cónyuge al cambiar estado ===
    const actualizarTitulo = async (cita, nuevoTitulo) => {
        try {
            const fi = toDay(pick(cita, "fechaInicio", "fecha_inicio"));
            const ff = toDay(pick(cita, "fechaFin", "fecha_fin"));

            const toDisponible = String(nuevoTitulo || "").toUpperCase() === "DISPONIBLE";

            // Base común
            const payloadBase = {
                titulo: nuevoTitulo,
                descripcion: toDisponible
                    ? removeBloqueadaSuffix(cita.descripcion)
                    : (cita.descripcion || cita.titulo || "Cita"),
                fechaInicio: isoLocal(fi),
                fechaFin: isoLocal(ff),
                fecha_inicio: isoLocal(fi),
                fecha_fin: isoLocal(ff),
            };

            // Colegiado: si es DISPONIBLE, se libera
            const colegCodigo =
                cita?.colegiado?.codigo ??
                cita?.colegiadoCodigo ??
                cita?.colegiado_id ??
                cita?.colegiadoId ??
                null;

            const payloadColegiado = toDisponible
                ? { colegiadoCodigo: null, colegiadoId: null, colegiado_id: null }
                : { colegiadoCodigo: colegCodigo, colegiadoId: colegCodigo, colegiado_id: colegCodigo };

            // Renta/Cónyuge: preservar al confirmar/pendiente; limpiar al dejar disponible
            const payloadRenta = toDisponible ? clearRentaConyuge() : extractRentaConyuge(cita);

            const payload = { ...payloadBase, ...payloadColegiado, ...payloadRenta };

            await axios.put(`${API}/api/citas/${cita.id}`, payload, { headers });
            await cargarCitas();
            setMensaje(`Cita #${cita.id} actualizada a ${nuevoTitulo}.`);
        } catch (e) {
            console.error(e);
            setMensaje("No se pudo actualizar la cita.");
        }
    };

    const esBloqueoAdmin = (cita) => {
        const desc = String(cita?.descripcion || "");
        return desc.includes(BLOQUEO_ADMIN_TAG);
    };

    const BLOQUEADA_SUFFIX = " - BLOQUEADA";

    const stripBloqueoAdminTag = (desc = "") =>
        String(desc || "").replace(BLOQUEO_ADMIN_TAG, "").trim();

    const ensureBloqueadaSuffix = (desc = "") => {
        const clean = stripBloqueoAdminTag(desc).trim();
        if (!clean) return `BLOQUEADA`;
        return clean.endsWith(BLOQUEADA_SUFFIX) ? clean : `${clean}${BLOQUEADA_SUFFIX}`;
    };

    const removeBloqueadaSuffix = (desc = "") => {
        let clean = String(desc || "")
            .replaceAll(BLOQUEO_ADMIN_TAG, "")
            .replace(/\s*-\s*BLOQUEADA\s*/gi, "")
            .trim();

        return clean || "Hueco disponible";
    };

    const buildBlockedDescription = (desc = "") => {
        const visible = ensureBloqueadaSuffix(desc);
        return `${BLOQUEO_ADMIN_TAG} ${visible}`.trim();
    };

    const getVisibleDescription = (desc = "") => stripBloqueoAdminTag(desc);

    const bloquearCitaAdmin = async (cita) => {
        const fi = toDay(pick(cita, "fechaInicio", "fecha_inicio"));
        const ff = toDay(pick(cita, "fechaFin", "fecha_fin"));

        const descripcionOriginal =
            cita.descripcion ||
            "Hueco disponible";

        const payload = {
            titulo: "CONFIRMADA",
            descripcion: buildBlockedDescription(descripcionOriginal),
            fechaInicio: isoLocal(fi),
            fechaFin: isoLocal(ff),
            fecha_inicio: isoLocal(fi),
            fecha_fin: isoLocal(ff),

            // sin colegiado real
            colegiadoCodigo: null,
            colegiadoId: null,
            colegiado_id: null,

            // limpia datos extra por si acaso
            ...clearRentaConyuge(),
        };

        await axios.put(`${API}/api/citas/${cita.id}`, payload, { headers });
    };

    const liberarCitaBloqueadaAdmin = async (cita) => {
        const fi = toDay(pick(cita, "fechaInicio", "fecha_inicio"));
        const ff = toDay(pick(cita, "fechaFin", "fecha_fin"));

        const payload = {
            titulo: "DISPONIBLE",
            descripcion: removeBloqueadaSuffix(cita.descripcion),
            fechaInicio: isoLocal(fi),
            fechaFin: isoLocal(ff),
            fecha_inicio: isoLocal(fi),
            fecha_fin: isoLocal(ff),

            colegiadoCodigo: null,
            colegiadoId: null,
            colegiado_id: null,

            ...clearRentaConyuge(),
        };

        await axios.put(`${API}/api/citas/${cita.id}`, payload, { headers });
    };

    // --- actualizar muchas citas en bloque
    const actualizarMasivo = async (lista, nuevoTitulo, mensajeConfirmacion = "¿Confirmar operación?") => {
        if (!lista?.length) return;
        const ok = window.confirm(mensajeConfirmacion);
        if (!ok) return;

        try {
            for (const c of lista) {
                // eslint-disable-next-line no-await-in-loop
                await actualizarTitulo(c, nuevoTitulo);
            }
            setMensaje(`Actualizadas ${lista.length} citas a ${nuevoTitulo}.`);
        } catch (e) {
            console.error(e);
            setMensaje("Error en la actualización masiva.");
        }
    };

    const bloquearTodosLosDisponibles = async () => {
        const disponibles = citas.filter(
            (c) => (c.titulo || "").toUpperCase() === "DISPONIBLE"
        );

        if (!disponibles.length) {
            setMensaje("No hay huecos disponibles para bloquear.");
            return;
        }

        const ok = window.confirm(`¿Bloquear ${disponibles.length} huecos disponibles?`);
        if (!ok) return;

        try {
            for (const c of disponibles) {
                // eslint-disable-next-line no-await-in-loop
                await bloquearCitaAdmin(c);
            }
            await cargarCitas();
            setMensaje(`Se bloquearon ${disponibles.length} huecos.`);
        } catch (e) {
            console.error(e);
            setMensaje("Error al bloquear los huecos.");
        }
    };

    const liberarBloqueosAdmin = async () => {
        const bloqueadas = citas.filter(
            (c) =>
                (c.titulo || "").toUpperCase() === "CONFIRMADA" &&
                esBloqueoAdmin(c)
        );

        if (!bloqueadas.length) {
            setMensaje("No hay citas bloqueadas por admin para liberar.");
            return;
        }

        const ok = window.confirm(`¿Liberar ${bloqueadas.length} huecos bloqueados por admin?`);
        if (!ok) return;

        try {
            for (const c of bloqueadas) {
                // eslint-disable-next-line no-await-in-loop
                await liberarCitaBloqueadaAdmin(c);
            }
            await cargarCitas();
            setMensaje(`Se liberaron ${bloqueadas.length} huecos bloqueados por admin.`);
        } catch (e) {
            console.error(e);
            setMensaje("Error al liberar los huecos bloqueados.");
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <Box p={2}>
                <Typography variant="h5" gutterBottom>Gestión de citas</Typography>

                {/* Calendario/condiciones */}
                <Accordion defaultExpanded={false}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">Calendario y condiciones</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Paper sx={{ p: 2 }}>
                            <Box sx={{ display: "grid", gap: 3 }}>
                                {/* Fechas */}
                                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                                    <DatePicker label="Desde" format="DD/MM/YYYY" value={config.fechaDesde}
                                        onChange={(v) => setConfig((c) => ({ ...c, fechaDesde: v }))} />
                                    <DatePicker label="Hasta" format="DD/MM/YYYY" value={config.fechaHasta}
                                        onChange={(v) => setConfig((c) => ({ ...c, fechaHasta: v }))} />
                                </Box>

                                {/* Laborables + tipo + horarios/duración/turnos POR DÍA */}
                                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 2 }}>
                                    {DIA_OPCIONES.map(d => {
                                        const checked = config.diasLaborables.includes(d.key);
                                        const tipoSel = (config.tiposSemana || {})[d.key] ?? TIPO_NONE;
                                        const per = config.perDia?.[d.key] || {};
                                        const setPer = (patch) => setConfig(c => ({
                                            ...c,
                                            perDia: { ...(c.perDia || {}), [d.key]: { ...(c.perDia?.[d.key] || {}), ...patch } }
                                        }));

                                        return (
                                            <Box key={d.key} sx={{ p: 1.25, border: "1px solid #eee", borderRadius: 1.5 }}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={checked}
                                                            onChange={(e) => {
                                                                const set = new Set(config.diasLaborables);
                                                                if (e.target.checked) set.add(d.key); else set.delete(d.key);
                                                                setConfig((c) => ({ ...c, diasLaborables: Array.from(set) }));
                                                            }}
                                                        />
                                                    }
                                                    label={d.label}
                                                />

                                                <FormControl fullWidth size="small" sx={{ mt: .5 }}>
                                                    <InputLabel>Tipo</InputLabel>
                                                    <Select
                                                        label="Tipo"
                                                        value={tipoSel}
                                                        onChange={(e) => changeTipoSemana(d.key, e.target.value)}
                                                        disabled={!checked}
                                                    >
                                                        <MenuItem value={TIPO_NONE}>Sin tipo</MenuItem>
                                                        {TIPOS_CITA.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                                    </Select>
                                                </FormControl>

                                                {/* Mañana */}
                                                <Box sx={{ display: "flex", gap: 1, mt: 1, alignItems: "center" }}>
                                                    <TimeField
                                                        label="Inicio (mañana)"
                                                        value={timeToDayjsOn(dayjs(), per.horaInicioManana || "09:00")}
                                                        onChange={(v) => setPer({ horaInicioManana: v ? dayjs(v).format("HH:mm") : "09:00" })}
                                                        disabled={!checked}
                                                        size="small"
                                                    />
                                                    <TimeField
                                                        label="Fin (mañana)"
                                                        value={timeToDayjsOn(dayjs(), per.horaFinManana || "14:00")}
                                                        onChange={(v) => setPer({ horaFinManana: v ? dayjs(v).format("HH:mm") : "14:00" })}
                                                        disabled={!checked}
                                                        size="small"
                                                    />
                                                </Box>

                                                {/* Tarde enable */}
                                                <FormControlLabel
                                                    sx={{ mt: .5 }}
                                                    control={
                                                        <Checkbox
                                                            checked={!!per.usarTarde}
                                                            onChange={(e) => setPer({ usarTarde: e.target.checked })}
                                                        />
                                                    }
                                                    label="Habilitar turno de tarde"
                                                    disabled={!checked}
                                                />

                                                {/* Tarde times */}
                                                <Box sx={{ display: "flex", gap: 1, alignItems: "center", opacity: per.usarTarde ? 1 : .6 }}>
                                                    <TimeField
                                                        label="Inicio (tarde)"
                                                        value={timeToDayjsOn(dayjs(), per.horaInicioTarde || "16:00")}
                                                        onChange={(v) => setPer({ horaInicioTarde: v ? dayjs(v).format("HH:mm") : "16:00" })}
                                                        disabled={!checked || !per.usarTarde}
                                                        size="small"
                                                    />
                                                    <TimeField
                                                        label="Fin (tarde)"
                                                        value={timeToDayjsOn(dayjs(), per.horaFinTarde || "19:00")}
                                                        onChange={(v) => setPer({ horaFinTarde: v ? dayjs(v).format("HH:mm") : "19:00" })}
                                                        disabled={!checked || !per.usarTarde}
                                                        size="small"
                                                    />
                                                </Box>

                                                {/* Duración y turnos por bloque */}
                                                <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                                                    <TextField
                                                        select
                                                        label="Duración (min)"
                                                        value={per.duracionMin ?? 15}
                                                        onChange={(e) => setPer({ duracionMin: Number(e.target.value) })}
                                                        sx={{ minWidth: 140 }}
                                                        size="small"
                                                        disabled={!checked}
                                                    >
                                                        {DURACIONES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                                                    </TextField>

                                                    <TextField
                                                        select
                                                        label="Turnos por bloque"
                                                        value={per.turnosPorBloque ?? 1}
                                                        onChange={(e) => setPer({ turnosPorBloque: Number(e.target.value) })}
                                                        sx={{ minWidth: 170 }}
                                                        size="small"
                                                        disabled={!checked}
                                                    >
                                                        {TURNOS.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                                                    </TextField>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>

                                {/* Permitir cónyuge (general) */}
                                <FormControlLabel
                                    control={<Checkbox checked={config.permitirConyuge}
                                        onChange={(e) => setConfig((c) => ({ ...c, permitirConyuge: e.target.checked }))} />}
                                    label="Permitir cita cónyuge"
                                />

                                {/* Festivos */}
                                <Box>
                                    <Typography variant="subtitle1" gutterBottom>Días festivos / no atención (se excluyen)</Typography>
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                        <TextField
                                            label="Añadir fecha (DD/MM/YYYY)"
                                            value={festivoInput}
                                            onChange={(e) => setFestivoInput(e.target.value)}
                                            placeholder={dayjs(config.fechaDesde).format("DD/MM/YYYY")}
                                            sx={{ maxWidth: 220 }}
                                        />
                                        <Button startIcon={<AddCircleOutlineIcon />} variant="outlined" onClick={addFestivo}>
                                            Añadir
                                        </Button>
                                    </Box>
                                    {(config.festivos?.length || 0) > 0 && (
                                        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                                            {config.festivos.map((f, idx) => {
                                                const ymd = dayjs(f).format("YYYY-MM-DD");
                                                const human = dayjs(f).format("DD/MM/YYYY");
                                                return <Chip key={ymd + idx} label={human} onDelete={() => removeFestivo(ymd)} />;
                                            })}
                                        </Stack>
                                    )}
                                </Box>

                                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                                    <Button variant="contained" onClick={generarHuecos} disabled={generando}>
                                        {generando ? "Generando…" : "Generar huecos"}
                                    </Button>
                                </Box>
                            </Box>
                        </Paper>
                    </AccordionDetails>
                </Accordion>

                <Box mt={2} display="grid" gridTemplateColumns={{ xs: "1fr", lg: "1fr 1fr" }} gap={2}>
                    {/* PENDIENTES */}
                    <Paper sx={{ p: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="h6">Citas PENDIENTES</Typography>
                            <Box display="flex" gap={1} flexWrap="wrap">
                                <Button size="small" variant="contained"
                                    onClick={() => actualizarMasivo(citas.filter(c => (c.titulo || "").toUpperCase() === "PENDIENTE"), "CONFIRMADA", `¿Confirmar ${pendientes.length} citas pendientes?`)}
                                    disabled={!pendientes.length}>
                                    Confirmar todas
                                </Button>
                                <Button size="small" variant="outlined"
                                    onClick={() => actualizarMasivo(citas.filter(c => (c.titulo || "").toUpperCase() === "PENDIENTE"), "DISPONIBLE", `¿Marcar ${pendientes.length} pendientes como DISPONIBLE?`)}
                                    disabled={!pendientes.length}>
                                    DISPONIBLE a todas
                                </Button>
                            </Box>
                        </Box>

                        {loadingCitas ? (
                            <Typography>Cargando…</Typography>
                        ) : pendientes.length === 0 ? (
                            <Typography>No hay pendientes.</Typography>
                        ) : (
                            <>
                                {pendSlice.map((c) => {
                                    const fi = toDay(pick(c, "fechaInicio", "fecha_inicio"));
                                    const ff = toDay(pick(c, "fechaFin", "fecha_fin"));
                                    const coleg = c?.colegiado?.codigo ?? c?.colegiadoCodigo ?? c?.colegiado_id ?? c?.colegiadoId ?? "-";
                                    const isExpanded = expandedId === c.id;
                                    return (
                                        <Box
                                            key={c.id}
                                            p={1.5}
                                            mb={1}
                                            border="1px solid #ddd"
                                            borderRadius={2}
                                            sx={{ cursor: "pointer", "&:hover": { background: "#fafafa" } }}
                                            onClick={() => setExpandedId((prev) => (prev === c.id ? null : c.id))}
                                        >
                                            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography><strong>{fi?.format("DD/MM/YYYY")}</strong> {fi?.format("HH:mm")}–{ff?.format("HH:mm")}</Typography>
                                                    <Typography variant="body2" fontSize="1rem" sx={{ opacity: .8, wordBreak: "break-word" }}>
                                                        Coleg.: <strong>{coleg}</strong> · {c.descripcion || "—"}
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" gap={1} onClick={(e) => e.stopPropagation()}>
                                                    <Button size="small" variant="contained" onClick={() => actualizarTitulo(c, "CONFIRMADA")}>
                                                        Confirmar
                                                    </Button>
                                                    <Button size="small" variant="outlined" onClick={() => actualizarTitulo(c, "DISPONIBLE")}>
                                                        DISPONIBLE
                                                    </Button>
                                                </Box>
                                            </Box>

                                            {isExpanded && <DetalleColegiado c={c} />}
                                        </Box>
                                    );
                                })}

                                <TablePagination
                                    component="div"
                                    count={pendientes.length}
                                    page={pendPage}
                                    onPageChange={(_, newPage) => setPendPage(newPage)}
                                    rowsPerPage={pendRows}
                                    onRowsPerPageChange={(e) => { setPendRows(parseInt(e.target.value, 10)); setPendPage(0); }}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                />
                            </>
                        )}
                    </Paper>

                    {/* CONFIRMADAS */}
                    <Paper sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography variant="h6">Citas CONFIRMADAS</Typography>
                            <Box display="flex" gap={1} flexWrap="wrap">
                                <Button size="small" variant="outlined" onClick={() => exportConfirmadasCSV(true)}>
                                    Exportar página (CSV)
                                </Button>
                                <Button size="small" variant="contained" onClick={() => exportConfirmadasCSV(false)}>
                                    Exportar todas (CSV)
                                </Button>
                                <Button size="small" variant="outlined"
                                    onClick={() => actualizarMasivo(confirmadas, "PENDIENTE", `¿Pasar ${confirmadas.length} confirmadas a PENDIENTE?`)}
                                    disabled={!confirmadas.length}>
                                    PENDIENTE a todas
                                </Button>
                                <Button size="small" variant="outlined"
                                    onClick={() => actualizarMasivo(confirmadas, "DISPONIBLE", `¿Marcar ${confirmadas.length} confirmadas como DISPONIBLE?`)}
                                    disabled={!confirmadas.length}>
                                    DISPONIBLE a todas
                                </Button>
                            </Box>
                        </Box>

                        {loadingCitas ? (
                            <Typography>Cargando…</Typography>
                        ) : confirmadas.length === 0 ? (
                            <Typography>No hay confirmadas.</Typography>
                        ) : (
                            <>
                                {confSlice.map((c) => {
                                    const fi = toDay(pick(c, "fechaInicio", "fecha_inicio"));
                                    const ff = toDay(pick(c, "fechaFin", "fecha_fin"));
                                    const coleg = c?.colegiado?.codigo ?? c?.colegiadoCodigo ?? c?.colegiado_id ?? c?.colegiadoId ?? "-";
                                    const isExpanded = expandedId === c.id;
                                    return (
                                        <Box
                                            key={c.id}
                                            p={1.5}
                                            mb={1}
                                            border="1px solid #ddd"
                                            borderRadius={2}
                                            sx={{ cursor: "pointer", "&:hover": { background: "#fafafa" } }}
                                            onClick={() => setExpandedId((prev) => (prev === c.id ? null : c.id))}
                                        >
                                            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography><strong>{fi?.format("DD/MM/YYYY")}</strong> {fi?.format("HH:mm")}–{ff?.format("HH:mm")}</Typography>
                                                    <Typography variant="body2" sx={{ opacity: .8, wordBreak: "break-word" }}>
                                                        {esBloqueoAdmin(c)
                                                            ? getVisibleDescription(c.descripcion) || "BLOQUEADA"
                                                            : `Coleg.: ${coleg} · ${c.descripcion || "—"}`}
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" gap={1} onClick={(e) => e.stopPropagation()}>
                                                    <Button size="small" variant="outlined" onClick={() => actualizarTitulo(c, "PENDIENTE")}>
                                                        PENDIENTE
                                                    </Button>
                                                    <Button size="small" variant="outlined" onClick={() => actualizarTitulo(c, "DISPONIBLE")}>
                                                        DISPONIBLE
                                                    </Button>
                                                </Box>
                                            </Box>

                                            {isExpanded && <DetalleColegiado c={c} />}
                                        </Box>
                                    );
                                })}

                                <TablePagination
                                    component="div"
                                    count={confirmadas.length}
                                    page={confPage}
                                    onPageChange={(_, newPage) => setConfPage(newPage)}
                                    rowsPerPage={confRows}
                                    onRowsPerPageChange={(e) => { setConfRows(parseInt(e.target.value, 10)); setConfPage(0); }}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                />
                            </>
                        )}
                    </Paper>
                </Box>

                {/* ========== ASIGNACIÓN DIRECTA (CABECERA AMPLIA) ========== */}
                <Paper sx={{ p: 3, mt: 2 }}>
                    <Typography variant="h6" gutterBottom>Asignar cita a colegiado</Typography>

                    {/* Fila grande: código + calendario + huecos */}
                    <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "300px 1fr" }} gap={3}>
                        <Box>
                            <TextField
                                fullWidth
                                label="Código de colegiado"
                                value={adminColegiadoCodigo}
                                onChange={(e) => setAdminColegiadoCodigo(e.target.value.replace(/\D/g, ""))}
                                size="medium"
                            />
                            <Typography variant="body2" sx={{ mt: 1, opacity: 0.7 }}>
                                Rango: {minFechaCitas ? minFechaCitas.format("DD/MM/YYYY") : "—"} – {maxFechaCitas ? maxFechaCitas.format("DD/MM/YYYY") : "—"}
                            </Typography>
                            <DateCalendar
                                value={selDate}
                                onChange={(v) => v && setSelDate(v)}
                                minDate={minFechaCitas || undefined}
                                maxDate={maxFechaCitas || undefined}
                                defaultCalendarMonth={minFechaCitas || dayjs()}
                            />
                        </Box>

                        <Box>
                            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                                Huecos DISPONIBLES del {dayjs(selDate).format("DD/MM/YYYY")}
                            </Typography>

                            {huecosDia.length === 0 ? (
                                <Typography>No hay huecos disponibles este día.</Typography>
                            ) : (
                                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 1.2 }}>
                                    {huecosDia.map((h) => {
                                        const fi = toDay(pick(h, "fechaInicio", "fecha_inicio"));
                                        const ff = toDay(pick(h, "fechaFin", "fecha_fin"));
                                        const selected = slotSel?.id === h.id;
                                        return (
                                            <Button
                                                key={h.id}
                                                variant={selected ? "contained" : "outlined"}
                                                onClick={() => setSlotSel(h)}
                                                size="large"
                                                sx={{ justifyContent: "space-between", fontWeight: 600 }}
                                            >
                                                <span>#{h.id}</span>
                                                <span>{fi?.format("HH:mm")}–{ff?.format("HH:mm")}</span>
                                            </Button>
                                        );
                                    })}
                                </Box>
                            )}

                            <Divider sx={{ my: 2 }} />
                            <Box display="flex" gap={1} flexWrap="wrap">
                                <Button
                                    variant="contained"
                                    color="warning"
                                    size="large"
                                    onClick={bloquearTodosLosDisponibles}
                                >
                                    Bloquear todos los huecos
                                </Button>

                                <Button
                                    variant="outlined"
                                    color="success"
                                    size="large"
                                    onClick={liberarBloqueosAdmin}
                                >
                                    Liberar huecos bloqueados
                                </Button>
                            </Box>

                            <Divider sx={{ my: 2 }} />
                            <Button
                                variant="contained"
                                size="large"
                                onClick={asignarHueco}
                                disabled={!slotSel || !hasAdminCode}
                            >
                                Asignar (CONFIRMADA)
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* ========== FORMULARIO DETALLADO (DEBAJO DEL CALENDARIO) ========== */}
                {hasAdminCode && (
                    <Paper sx={{ p: 3, mt: 2 }}>
                        <Typography variant="h6" gutterBottom>Datos de la asignación</Typography>

                        <TextField
                            fullWidth
                            label="Descripción (opcional)"
                            multiline
                            minRows={2}
                            value={adminDescripcion}
                            onChange={(e) => setAdminDescripcion(e.target.value)}
                            sx={{ mb: 2 }}
                        />

                        <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>Datos Renta (Titular)</Typography>
                        <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
                            <DatePicker
                                label="Caducidad NIF (titular)"
                                format="DD/MM/YYYY"
                                value={fechaCaducidadNif}
                                onChange={setFechaCaducidadNif}
                            />
                            <TextField
                                label="Referencia borrador (titular)"
                                value={referenciaBorrador}
                                onChange={(e) => setReferenciaBorrador(e.target.value)}
                            />
                        </Box>
                        <FormControlLabel
                            sx={{ mt: 1 }}
                            control={<Checkbox checked={rentaAnterior} onChange={(e) => setRentaAnterior(e.target.checked)} />}
                            label="Hizo la renta con nosotros el año pasado"
                        />

                        <Divider sx={{ my: 2 }} />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={usarConyuge}
                                    onChange={(e) => setUsarConyuge(e.target.checked)}
                                    disabled={!config.permitirConyuge}
                                />
                            }
                            label="Cita con cónyuge (reserva hueco consecutivo)"
                        />

                        {usarConyuge && (
                            <Box sx={{ border: "1px dashed #ddd", borderRadius: 2, p: 2, mt: 1 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>Datos del cónyuge</Typography>
                                <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
                                    <TextField
                                        label="Nombre cónyuge"
                                        value={nombreConyuge}
                                        onChange={(e) => setNombreConyuge(e.target.value)}
                                    />
                                    <TextField
                                        label="Apellidos cónyuge"
                                        value={apellidosConyuge}
                                        onChange={(e) => setApellidosConyuge(e.target.value)}
                                    />
                                    <TextField
                                        label="NIF cónyuge"
                                        value={nifConyuge}
                                        onChange={(e) => setNifConyuge(e.target.value)}
                                    />
                                    <DatePicker
                                        label="Caducidad NIF cónyuge"
                                        format="DD/MM/YYYY"
                                        value={fechaCaducidadNifConyuge}
                                        onChange={setFechaCaducidadNifConyuge}
                                    />
                                    <TextField
                                        label="Referencia borrador (cónyuge)"
                                        value={referenciaBorradorConyuge}
                                        onChange={(e) => setReferenciaBorradorConyuge(e.target.value)}
                                    />
                                </Box>
                            </Box>
                        )}
                        <Typography variant="body2" sx={{ mt: 2, opacity: .7 }}>
                            Consejo: primero selecciona el hueco en la parte superior y luego pulsa “Asignar”.
                        </Typography>
                    </Paper>
                )}

                <Snackbar open={!!mensaje} autoHideDuration={4200} onClose={() => setMensaje("")} message={mensaje} />
            </Box>
        </LocalizationProvider>
    );
}
